import { createContext, useState } from "react";
import { fetchJSON } from "./utils/network";
import PanelError from "./widget/panel-error";
import PanelWaiting from "./widget/panel-waiting";

// For using it in other components
export const RequestContext = createContext<{
  asyncFunction: <T>(
    fn: () => Promise<T>,
    options: {
      message: string;
    }
  ) => Promise<T>;
  fetchJSONAndMessage: <T>(
    message: string,
    request: Request
  ) => Promise<T | null>;
} | null>(null);

// For the main app, only once
export const RequestContextProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const reset = () => {
    setLoadingMessage(null);
    setError(null);
  };

  function asyncFunction<T = unknown>(
    fn: () => Promise<T>,
    options: { message: string }
  ): Promise<T> {
    reset();
    setLoadingMessage(options.message);

    return fn()
      .catch((error) => {
        setError(error);
        throw error;
      })
      .finally(() => {
        setLoadingMessage(null);
      });
  }

  function fetchJSONAndMessage<T = unknown>(
    message: string,
    request: Request
  ): Promise<T | null> {
    return asyncFunction<T | null>(() => fetchJSON<T>(request), { message });
  }

  return (
    <RequestContext.Provider
      value={{
        asyncFunction: asyncFunction,
        fetchJSONAndMessage
      }}
    >
      {error && (
        <PanelError error={error} acknowledged={() => setError(null)} />
      )}
      {children}
      {loadingMessage && <PanelWaiting message={loadingMessage} />}
    </RequestContext.Provider>
  );
};
